import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import procurementDocumentsService from '../../../services/procurementDocumentsService';
import VendorPicker from './VendorPicker';
import DocumentDownloadButtons from './DocumentDownloadButtons';
import PurchaseOrderDocumentView from './PurchaseOrderDocumentView';

const emptyItem = () => ({ description: '', units: '', qty: '', unit: '', unit_price: '', line_total: '' });

const PurchaseOrderFormModal = ({ purchaseRequest, onClose }) => {
  const [form, setForm] = useState({
    supplier_id: null,
    supplier_account_id: null,
    ship_to_company: '',
    ship_to_address: '',
    ship_to_contact: '',
    po_date: new Date().toISOString().slice(0, 10),
    requisition_ref: purchaseRequest.request_id,
    buyer_name: '',
    delivery_date_required: '',
    shipping_method: '',
    payment_terms: '',
    currency: 'NGN',
    tax: 0,
    shipping: 0,
    terms_conditions:
      "1. Goods/services must conform to the specifications and quantities stated above.\n2. Vendor must reference this PO number on all invoices and shipping documents.\n3. This PO is subject to the buyer's standard purchasing terms and conditions.",
  });
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [showView, setShowView] = useState(false);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const onVendorSelect = ({ vendor, account }) => {
    setForm((p) => ({
      ...p,
      supplier_id: vendor?.id || null,
      supplier_account_id: account?.id || null,
    }));
  };

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === 'qty' || field === 'unit_price') {
        const qty = Number(next[index].qty) || 0;
        const price = Number(next[index].unit_price) || 0;
        next[index].line_total = qty * price;
      }
      return next;
    });
  };

  const subtotal = items.reduce((sum, it) => sum + (Number(it.line_total) || 0), 0);
  const total = subtotal + (Number(form.tax) || 0) + (Number(form.shipping) || 0);

  const handleSave = async () => {
    if (!items.some((it) => it.description.trim())) {
      toast.error('Add at least one item');
      return;
    }
    try {
      setSaving(true);
      const res = await procurementDocumentsService.createPurchaseOrder({
        purchase_request_id: purchaseRequest.id,
        ...form,
        items: items.filter((it) => it.description.trim()),
      });
      setSaved(res.data.data.purchaseOrder);
      toast.success('Purchase Order created');
    } catch (error) {
      console.error('Create PO error:', error);
      toast.error(error.response?.data?.message || 'Failed to create purchase order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
      <div className="relative top-6 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Purchase Order - {purchaseRequest.request_id}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {saved ? (
          <div className="text-center py-8">
            <p className="text-green-700 font-medium mb-1">PO {saved.po_number} created</p>
            <p className="text-sm text-gray-500 mb-4">Download the document below.</p>
            <div className="flex justify-center">
              <DocumentDownloadButtons
                onView={() => setShowView(true)}
                onDownloadDocx={() => procurementDocumentsService.downloadPurchaseOrderDocx(saved.id, `${saved.po_number}.docx`)}
              />
            </div>
            <button onClick={onClose} className="mt-4 text-sm text-gray-500 hover:text-gray-700">
              Close
            </button>
            {showView && <PurchaseOrderDocumentView po={saved} onClose={() => setShowView(false)} />}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Supplier / Vendor</h4>
              <VendorPicker onSelect={onVendorSelect} />
            </div>

            <div className="border-t pt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Ship To</h4>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Ship To Company" value={form.ship_to_company} onChange={(e) => set('ship_to_company', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Receiving Contact" value={form.ship_to_contact} onChange={(e) => set('ship_to_contact', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Delivery Address" value={form.ship_to_address} onChange={(e) => set('ship_to_address', e.target.value)} className="col-span-2 px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="border-t pt-3 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">PO Date</label>
                <input type="date" value={form.po_date} onChange={(e) => set('po_date', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Buyer Name</label>
                <input value={form.buyer_name} onChange={(e) => set('buyer_name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Delivery Date Required</label>
                <input type="date" value={form.delivery_date_required} onChange={(e) => set('delivery_date_required', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Shipping Method</label>
                <input value={form.shipping_method} onChange={(e) => set('shipping_method', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Terms</label>
                <input value={form.payment_terms} onChange={(e) => set('payment_terms', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                <input value={form.currency} onChange={(e) => set('currency', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Order Details</h4>
                <button onClick={() => setItems((p) => [...p, emptyItem()])} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                  <Plus className="h-3.5 w-3.5" /> Add Item
                </button>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <input placeholder="Description" value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="col-span-4 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Units" value={it.units} onChange={(e) => updateItem(i, 'units', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input type="number" placeholder="Qty" value={it.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Unit" value={it.unit} onChange={(e) => updateItem(i, 'unit', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input type="number" placeholder="Unit Price" value={it.unit_price} onChange={(e) => updateItem(i, 'unit_price', e.target.value)} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <div className="col-span-2 text-xs text-gray-600 text-right">{Number(it.line_total || 0).toLocaleString()}</div>
                    <button onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="col-span-1 flex justify-center text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-2 space-y-1 text-right text-sm">
                <div className="flex justify-end gap-2 items-center">
                  <span className="text-gray-500">Tax:</span>
                  <input type="number" value={form.tax} onChange={(e) => set('tax', e.target.value)} className="w-28 px-2 py-1 border border-gray-300 rounded text-xs text-right" />
                </div>
                <div className="flex justify-end gap-2 items-center">
                  <span className="text-gray-500">Shipping:</span>
                  <input type="number" value={form.shipping} onChange={(e) => set('shipping', e.target.value)} className="w-28 px-2 py-1 border border-gray-300 rounded text-xs text-right" />
                </div>
                <p className="text-gray-600">Subtotal: ₦{subtotal.toLocaleString()}</p>
                <p className="font-semibold">TOTAL: ₦{total.toLocaleString()}</p>
              </div>
            </div>

            <div className="border-t pt-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Terms & Conditions</label>
              <textarea value={form.terms_conditions} onChange={(e) => set('terms_conditions', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs" />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Create Purchase Order'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrderFormModal;
